/**
 * @typedef {"polite" | "assertive" | ""} ariaLiveTypes
 */

/**
 * @param {object} options - The options for generating ARIA attributes.
 * @param {boolean|string} [options.ariaRequired] - Indicates whether the element is required for accessibility.
 * @param {boolean} [options.ariaExpanded] - Indicates whether the element is expanded.
 * @param {boolean} [options.ariaHidden] - Indicates whether the element is hidden from assistive technologies.
 * @param {boolean} [options.ariaInvalid] - Indicates whether the element has an incorrect value.
 * @param {boolean} [options.checked] - Indicates whether the element is checked.
 * @param {string} [options.ariaLabel] - The text alternative for the element.
 * @param {string} [options.ariaLabelledBy] - The ID of the element that labels the current element.
 * @param {boolean} [options.ariaMultiSelectable] - Indicates whether the element allows multiple selections.
 * @param {string} [options.ariaControls] - The ID of the element that the current element controls.
 * @param {ariaLiveTypes} [options.ariaLive] - Indicates the live region status of the element.
 * @returns {object} - The ARIA attributes generated based on the provided options.
 */
const useAria = ({
    ariaRequired = false,
    ariaExpanded = false,
    ariaInvalid = false,
    ariaHidden = false,
    checked = false,
    ariaLabel = "",
    ariaLabelledBy = "",
    ariaControls = "",
    ariaLive = "",
    ariaMultiSelectable = false,
}) => {
    const ariaProps = {};

    if (ariaRequired && ariaRequired !== "false") {
        ariaProps["aria-required"] = String(ariaRequired);
    }

    if (ariaExpanded) {
        ariaProps["aria-expanded"] = String(checked);
    }

    if (ariaLabel) {
        ariaProps["aria-label"] = ariaLabel;
    }

    if (ariaLabelledBy) {
        ariaProps["aria-labelledby"] = ariaLabelledBy;
    }

    if (ariaControls) {
        ariaProps["aria-controls"] = ariaControls;
    }

    if (ariaLive) {
        ariaProps["aria-live"] = ariaLive;
    }

    if (ariaInvalid) {
        ariaProps["aria-invalid"] = String(ariaInvalid);
    }

    if (ariaHidden) {
        ariaProps["aria-hidden"] = String(ariaHidden);
    }

    if (ariaMultiSelectable) {
        ariaProps["aria-multiselectable"] = String(ariaMultiSelectable);
    }

    return ariaProps;
};

export default useAria;
